import { useState } from 'react'
import type { JSX } from 'react'

import {
  Avatar,
  Card,
  CodeXmlIcon,
  DropdownMenu,
  EllipsisVerticalIcon,
  Flex,
  IconButton,
  KeyRoundIcon,
  PencilIcon,
  UnstyledButton,
  Text,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { formatDate } from '../../helpers/formatDate'
import { CredentialsDialog } from '../CredentialsDialog'

import styles from './APIClientInfo.module.css'

interface APIClient {
  clientId: string
  clientName: string
  clientSecret?: string | null
  createdAt: string
  logoUri?: string | null
}

interface APIClientInfoProps {
  client: APIClient
  onEdit: () => void
  onDelete: () => void
}

export const APIClientInfo = ({
  client,
  onEdit,
  onDelete,
}: APIClientInfoProps): JSX.Element => {
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false)
  const clientSecret = client.clientSecret

  return (
    <>
      <Card padding="none" className={styles.clientCard}>
        <div className={styles.clientRow}>
          {client.logoUri ? (
            <Avatar
              size="medium"
              alt={`${client.clientName} logo`}
              src={client.logoUri}
            />
          ) : (
            <div className={styles.iconBadge}>
              <CodeXmlIcon size="medium" />
            </div>
          )}

          <UnstyledButton className={styles.content} onClick={onEdit}>
            <Text className={styles.clientName}>{client.clientName}</Text>
            <Text className={styles.clientDate}>
              Created {formatDate(client.createdAt)}
            </Text>
          </UnstyledButton>

          <Flex gap="xs" className={styles.actions}>
            <IconButton
              label="View credentials"
              variant="tertiary"
              onClick={(): void => setIsCredentialsOpen(true)}
            >
              <KeyRoundIcon />
            </IconButton>

            <DropdownMenu>
              <DropdownMenu.Trigger>
                <IconButton label="More actions" variant="tertiary">
                  <EllipsisVerticalIcon />
                </IconButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item onSelect={onEdit}>
                  <PencilIcon />
                  Edit
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={onDelete} variant="critical">
                  <TrashIcon />
                  Delete
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </Flex>
        </div>
      </Card>

      <CredentialsDialog
        open={isCredentialsOpen}
        onOpenChange={setIsCredentialsOpen}
        clientId={client.clientId}
        clientSecret={clientSecret}
      />
    </>
  )
}
